import React, { useEffect, useCallback, useState } from "react";

import {
    Box,
    Paper,
    Divider,
    TextField,
    Grid,
    Typography,
    Switch,
    FormControlLabel,
    styled,
    Card,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
    InputLabel,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Chip,
    List,
    ListItem
} from "@mui/material";

import {
    LoadingButton
} from "@mui/lab";

import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import CommonBreadcumbs from "../../common/CommonBreadcumbs";
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonRestricted from "../../common/CommonRestricted";

import { spliceString } from "../../../utils/formatString";
import { mergeSchema } from "../../../utils/dataSchema";

import {
    useWallets, targetTypes, currencyTypes
} from "../../../hooks/api/useWallets";

import {
    useLocations, locationTypes, targetTypes as locationTargetTypes
} from "../../../hooks/api/useLocations";
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useAppContextFeatures } from "../../../context/AppContext";
import {
    initialValues, validationSchema
} from "../../../schema/wallets/walletSchema";

import { useSystemRoutes, routeTypes } from "../../../hooks/useSystemRoutes";
import { useSystemHead, headTitles } from "../../../hooks/useSystemHead";
import {
    usePermissions, scopeIds, scopeTypes, functionTypes
} from "../../../hooks/usePermissions";
import { useTranslations } from "../../../hooks/useTranslations";

import { useStateCustomFields } from "../../../hooks/state/useStateCustomFields";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function UpdateWallet({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@wallet"] });

    const { id } = useParams();

    const { promptMessage } = useSystemPrompt();
    const { isLoading, get, update, filterOperatorTypes } = useWallets({ targetType: targetTypes.wallet });
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.wallet.id,
        scopeTypes.wallet.wallet.id
    );

    const { setSystemLoading, openCustomDrawer } = useAppContextFeatures();

    const navigate = useNavigate();
    const [savedValues, setSavedValues] = useState(null);
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType);
    // const [checked, setChecked] = useState(false);

    const getSchema = () => {
        return validationSchema;
    };

    const loadData = useCallback(async (id) => {

        setSystemLoading(true);

        try {

            const { data } = await get({ id });
            console.log("data", data);
            const { wallet } = data;
            console.log("wallet", wallet);
            setSavedValues({
                walletName: wallet.name,
                walletKey: wallet.wallet_key,
                currency: wallet.currency,
                conversionRate: wallet.conversion_rate,
                primaryWallet: wallet.primary_wallet === "1",
                description: wallet.description,
                status: wallet?.status === "1"
            });

        } catch (error) {
            promptMessage(false, `${error?.message}`);
        }

        setSystemLoading(false);

    }, [id]);

    useEffect(() => {
        loadData(id);
    }, []);

    const onSubmit = async (currentValues) => {

        try {

            const { rows } = values;
            let data = {
                wallet: {
                    name: currentValues?.walletName,
                    wallet_key: currentValues?.walletKey,
                    currency: currentValues?.currency,
                    primary_wallet: currentValues?.primaryWallet ? 1 : 0,
                    conversion_rate: currentValues?.conversionRate,
                    description: currentValues?.description,
                    status: currentValues?.status ? 1 : 0
                }
            };
            const response = await update(
                { id, data, scopeType: "", targetType: targetTypes.wallet });

            promptMessage(true, `${response?.message}`);
            navigate(getRoute("settings/wallets"));

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`);
        }
    };

    const formik = useFormik({
        initialValues: savedValues,
        validationSchema: getSchema(),
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    const RenderForm = () => {

        if (!values) return <></>;

        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={0.5}>

                        <Grid item xs={12} sm={12} md={3} textAlign={"left"}>

                            <CommonSectionHeader
                                title={t("walletInfo")}
                                subTitle={t("detailsOfWallet")}
                            />

                        </Grid>
                        <Grid item xs={12} sm={12} md={9}>

                            <Grid container spacing={1}>
                                <Grid item xs={12} sm={12} md={6}>
                                    <FormControlLabel
                                        sx={{
                                            marginLeft: "1px"
                                        }}
                                        control={
                                            <Switch
                                                sx={{
                                                    mt: 1
                                                }}
                                                name="status"
                                                checked={Boolean(
                                                    values.status)}
                                                onChange={(
                                                    event, checked) => {
                                                    setFieldValue(
                                                        "status",
                                                        checked
                                                    );
                                                }}
                                            />
                                        }
                                        label={
                                            Boolean(values.status) ?
                                                t("enabled") :
                                                t("disabled")
                                        }

                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <></>
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        autoFocus
                                        fullWidth
                                        disabled
                                        name={"walletName"}
                                        label={t("walletName")}
                                        type="text"
                                        variant="outlined"
                                        value={values.walletName}
                                        onChange={handleChange}
                                        error={touched.walletName &&
                                            Boolean(errors.walletName)}
                                        helperText={touched.walletName &&
                                            errors.walletName}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        autoFocus
                                        fullWidth
                                        name={"walletKey"}
                                        label={t("walletKey")}
                                        type="text"
                                        variant="outlined"
                                        value={values.walletKey}
                                        onChange={handleChange}
                                        error={touched.walletKey &&
                                            Boolean(errors.walletKey)}
                                        helperText={touched.walletKey &&
                                            errors.walletKey}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <FormControl
                                        margin="dense"
                                        fullWidth
                                        size="small"
                                        error={touched.currency &&
                                            Boolean(errors.currency)}
                                    >
                                        <InputLabel id="currency">{t(
                                            "currency")}</InputLabel>
                                        <Select
                                            labelId="currency"
                                            label={t("currency")}
                                            name={"currency"}
                                            disabled
                                            value={values.currency}
                                            sx={{
                                                textAlign: "left"
                                            }}
                                        >
                                            {

                                                Object.keys(currencyTypes).
                                                    map((i, index) => {
                                                        return (
                                                            <MenuItem
                                                                key={index}
                                                                value={currencyTypes[i]}>
                                                                {currencyTypes[i]}
                                                            </MenuItem>
                                                        );
                                                    })
                                            }
                                        </Select>
                                        <FormHelperText>
                                            {touched.currency &&
                                                errors.currency}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        autoFocus
                                        fullWidth
                                        name={"conversionRate"}
                                        label={t("conversionRate")}
                                        type="text"
                                        variant="outlined"
                                        value={values.conversionRate}
                                        onChange={handleChange}
                                        error={touched.conversionRate &&
                                            Boolean(
                                                errors.conversionRate)}
                                        helperText={touched.conversionRate &&
                                            errors.conversionRate}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <FormControlLabel
                                        sx={{
                                            marginLeft: "1px"
                                        }}
                                        control={
                                            <Switch
                                                sx={{
                                                    mt: 1
                                                }}
                                                name="primaryWallet"
                                                checked={Boolean(
                                                    values.primaryWallet)}
                                                onChange={(
                                                    event, checked) => {
                                                    setFieldValue(
                                                        "primaryWallet",
                                                        checked
                                                    );
                                                }}
                                            />
                                        }
                                        label={
                                            Boolean(values.primaryWallet) ?
                                                t("isPrimaryWallet") :
                                                t("notPrimaryWallet")
                                        }

                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"description"}
                                        label={t("description")}
                                        type="text"
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        value={values.description}
                                        onChange={handleChange}
                                        error={touched.description &&
                                            Boolean(errors.description)}
                                        helperText={touched.description &&
                                            errors.description}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </StyledCard>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t("saveChanges")}
                        </LoadingButton>
                    </Grid>

                </Grid>
            </form>

        );
    };

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.updateWallet })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("updateWallet")}
                breadcrumbs={[
                    {
                        displayText: t("wallets"),
                        props: {
                            href: getRoute("settings/wallets")
                        }
                    },
                    {
                        displayText: t("updateWallet"),
                        props: {
                            color: "secondary.main"
                        }
                    }
                ]}
            />

            {
                isPermissionAllow(
                    { functionType: functionTypes.pageUpdate.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    );
}